import { useCallback, useMemo, useState } from 'react'
import { getCountForm } from '@ucheba/utils/helpers/number'
import { EProgramTypes } from '@ucheba/utils/types/program'
import { useRouter } from '@ucheba/utils/hooks/useRouter'
import { ESpecialitiesDataKeys } from '@ucheba/store/specialities/search/types'
import { stringifyUrl } from '@ucheba/utils/helpers/router'
import { allRussiaLocationId } from '@ucheba/utils/constants/core'
import IconPhysics from '@ucheba/ui/icons/icon__atom.svg'
import IconMathematics from '@ucheba/ui/icons/icon__math.svg'
import IconRussian from '@ucheba/ui/icons/icon__russian.svg'
import IconInformatics from '@ucheba/ui/icons/icon__mouse.svg'
import IconSocial from '@ucheba/ui/icons/icon__socium.svg'
import IconGeography from '@ucheba/ui/icons/icon__geo.svg'
import IconEnglish from '@ucheba/ui/icons/icon__english.svg'
import IconChemistry from '@ucheba/ui/icons/icon__chimstery.svg'
import IconLiterature from '@ucheba/ui/icons/icon__pero.svg'
import IconHistory from '@ucheba/ui/icons/icon__history.svg'
import IconBiology from '@ucheba/ui/icons/icon__dnk.svg'
import IconForeignlanguage from '@ucheba/ui/icons/icon__lang-message.svg'
import { specialitiesSearchSelectors } from '@ucheba/store/specialities/search'
import { useSelector } from 'react-redux'
import { specialityPath } from './contants'
import {
  ISpecialitiesListItemProps,
  IUseSpecialitiesListItemCore,
  IUseToggleEgeSets,
} from './types'

export const useSpecialityHref = (id: ISpecialitiesListItemProps['id']): string => {
  return useMemo(() => `${specialityPath}/${id}`, [id])
}

export const useToggleEgeSets: IUseToggleEgeSets = (egeSets) => {
  const [shownAllEge, toggleShownAllEge] = useState(egeSets.length === 1)
  const showAllEge = useCallback(() => toggleShownAllEge(true), [])

  return {
    showAllEge,
    shownAllEge,
  }
}

export const getIsForInstitution = (specialityType: number): boolean => {
  return (
    specialityType === EProgramTypes.higherBachelor ||
    specialityType === EProgramTypes.higherSpeciality
  )
}

export const useSpecialitiesListItemCore: IUseSpecialitiesListItemCore = ({
  id,
  examSetList,
  specialityType,
  addSearchQueryToInstitutionHref,
  stats,
}) => {
  const criteria = useSelector(specialitiesSearchSelectors.criteria)

  const { query, pathname } = useRouter()

  /** Специальность для вуза */
  const isForInstitution = useMemo(
    () => getIsForInstitution(specialityType.value),
    [specialityType.value]
  )

  const institutions = useMemo(
    () => ({
      entity: stats?.institutionsCount,
      clarification: getCountForm(
        stats?.institutionsCount,
        isForInstitution ? ['вуз', 'вуза', 'вузов'] : ['колледж', 'колледжа', 'колледжей']
      ),
    }),
    [stats?.institutionsCount, isForInstitution]
  )

  const budget = useMemo(
    () => ({
      entity: stats?.budgetPlacesCount,
      clarification: getCountForm(stats?.budgetPlacesCount, [
        'бюджетное место',
        'бюджетных места',
        'бюджетных мест',
      ]),
    }),
    [stats?.budgetPlacesCount]
  )

  const score = useMemo(() => {
    const preparedScore =
      stats?.minPassingScore === stats?.maxPassingScore
        ? stats?.minPassingScore
        : `${stats?.minPassingScore}-${stats?.maxPassingScore}`

    return {
      entity: preparedScore || '',
      clarification: 'проходной балл',
    }
  }, [stats?.maxPassingScore, stats?.minPassingScore])

  const examsSts = useMemo(() => {
    return examSetList.map(({ subjects, institutionsCount: targetInstitutionsCount }) => {
      return {
        title: `Экзамены в ${targetInstitutionsCount} ${getCountForm(
          targetInstitutionsCount,
          isForInstitution
            ? ['вузе', 'вузах', 'вузах']
            : ['колледже', 'колледжах', 'колледжах']
        )}:`,
        subjects,
      }
    })
  }, [examSetList, isForInstitution])

  const level = useMemo(() => specialityType.name, [specialityType.name])
  const { showAllEge, shownAllEge } = useToggleEgeSets(examsSts)
  const href = useSpecialityHref(id)

  const getTrainingFormTypesValues = useCallback((values: any[] | null) => {
    return values?.reduce((acc, item, index) => {
      if (typeof item === 'object' && 'value' in item) {
        acc[`tf[${index}]`] = String(item.value)
      } else {
        acc[`tf[${index}]`] = String(item)
      }

      return acc
    }, {})
  }, [])

  const getExamSubjectValues = useCallback((values: Array<string | number> | null) => {
    if (values && values.length > 0) {
      return values.reduce(
        (acc, item) => {
          acc[`ege_${item}_set`] = 1

          return acc
        },
        {
          o: 'point',
          ege: 1,
          ege_add_exam: 1,
        }
      )
    }
    return {}
  }, [])

  const getLocationsValues = useCallback((values: Array<string | number> | null) => {
    return values?.reduce((acc, item, index) => {
      acc[`eq[${index}]`] = `__l:${item}`

      return acc
    }, {})
  }, [])

  const institutionsHref = useMemo(() => {
    if (criteria || (addSearchQueryToInstitutionHref && Object.keys(query).length)) {
      const specialityValue = {
        'eq[-1]': `__sp:${id}`,
      }

      const trainingFormTypesValues = criteria
        ? getTrainingFormTypesValues(criteria.trainingFormTypes)
        : getTrainingFormTypesValues(
            Array.from(query[ESpecialitiesDataKeys.trainingFormTypes] || [])
          )

      const examSubjectIdsValues = criteria
        ? getExamSubjectValues(criteria.examSubjectIds)
        : getExamSubjectValues(
            Array.from(query[ESpecialitiesDataKeys.examSubjectIds] || [])
          )

      const locationIdsValues = criteria
        ? getLocationsValues(criteria.locationIds)
        : getLocationsValues(
            Array.from<string | number>(
              query[ESpecialitiesDataKeys.locationIds] || [allRussiaLocationId]
            )
          )

      if (pathname.endsWith('higher') || pathname.endsWith('vocational')) {
        return `/for-abiturients/${isForInstitution ? 'vuz' : 'college'}/spec/${id}`
      }

      return stringifyUrl(`/for-abiturients/${isForInstitution ? 'vuz' : 'college'}`, {
        set: {
          ...specialityValue,
          ...trainingFormTypesValues,
          ...examSubjectIdsValues,
          ...locationIdsValues,
        },
      })
    }

    return `/for-abiturients/${isForInstitution ? 'vuz' : 'college'}/spec/${id}`
  }, [
    criteria,
    addSearchQueryToInstitutionHref,
    query,
    isForInstitution,
    id,
    getTrainingFormTypesValues,
    getExamSubjectValues,
    getLocationsValues,
    pathname,
  ])

  const icons = useMemo(() => {
    return {
      mathematics: IconMathematics,
      russian: IconRussian,
      informatics: IconInformatics,
      foreignlanguage: IconForeignlanguage,
      social: IconSocial,
      physics: IconPhysics,
      geography: IconGeography,
      english: IconEnglish,
      chemistry: IconChemistry,
      literature: IconLiterature,
      history: IconHistory,
      biology: IconBiology,
    }
  }, [])

  return {
    institutions,
    budget,
    score,
    examsSts,
    level,
    showAllEge,
    shownAllEge,
    href,
    institutionsHref,
    icons,
  }
}
